// inactivity.service.ts
import { Injectable, NgZone, OnDestroy } from '@angular/core';

export type InactivityLogoutReason = 'timeout' | 'storage';

@Injectable({
  providedIn: 'root',
})
export class InactivityService implements OnDestroy {
  private readonly inactivityLimit = 20 * 60 * 1000; // 20 minutes
  private readonly activityStorageKey = 'smzh:last-activity';
  private readonly logoutStorageKey = 'force-logout';

  private timerId: ReturnType<typeof setTimeout> | null = null;
  private lastActivityTimestamp = Date.now();
  private logoutCallback?: (reason: InactivityLogoutReason) => void;
  private watching = false;

  private readonly events: Array<keyof WindowEventMap> = [
    'mousedown',
    'mousemove',
    'click',
    'keydown',
    'scroll',
    'touchstart',
    'touchmove',
    'focus',
  ];

  private readonly boundActivityListener = this.handleLocalActivity.bind(this);
  private readonly storageHandler = this.onStorageEvent.bind(this);

  constructor(private readonly ngZone: NgZone) {}

  registerLogoutCallback(callback: (reason: InactivityLogoutReason) => void): void {
    this.logoutCallback = callback;
  }

  startWatching(): void {
    if (this.watching) {
      return;
    }

    this.initializeLastActivity();
    this.ngZone.runOutsideAngular(() => {
      this.events.forEach((eventName) => {
        window.addEventListener(eventName, this.boundActivityListener, { passive: true });
      });

      window.addEventListener('storage', this.storageHandler);
    });

    this.handleLocalActivity();
    this.watching = true;
  }

  stopWatching(): void {
    if (!this.watching) {
      return;
    }

    this.events.forEach((eventName) => {
      window.removeEventListener(eventName, this.boundActivityListener);
    });

    window.removeEventListener('storage', this.storageHandler);

    if (this.timerId) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }

    this.watching = false;
  }

  private initializeLastActivity(): void {
    const stored = localStorage.getItem(this.activityStorageKey);
    const parsed = stored ? Number(stored) : NaN;
    const now = Date.now();

    if (Number.isFinite(parsed)) {
      this.lastActivityTimestamp = parsed;
    } else {
      this.lastActivityTimestamp = now;
      this.persistActivity(now);
    }
  }

  private handleLocalActivity(): void {
    this.applyActivity(Date.now(), { broadcast: true, allowEqualTimestamp: false });
  }

  private handleRemoteActivity(timestamp: number): void {
    const effectiveTimestamp = Math.max(timestamp, Date.now());
    this.applyActivity(effectiveTimestamp, { broadcast: false, allowEqualTimestamp: true });
  }

  private applyActivity(
    timestamp: number,
    options: { broadcast: boolean; allowEqualTimestamp: boolean }
  ): void {
    const { broadcast, allowEqualTimestamp } = options;
    const previousTimestamp = this.lastActivityTimestamp;

    if (!allowEqualTimestamp && timestamp <= previousTimestamp) {
      this.scheduleNextCheck();
      return;
    }

    this.lastActivityTimestamp = Math.max(timestamp, previousTimestamp);
    this.scheduleNextCheck();

    if (broadcast && this.lastActivityTimestamp > previousTimestamp) {
      this.persistActivity(this.lastActivityTimestamp);
    }
  }

  private scheduleNextCheck(): void {
    if (this.timerId) {
      clearTimeout(this.timerId);
    }

    const elapsed = Date.now() - this.lastActivityTimestamp;
    const remaining = Math.max(this.inactivityLimit - elapsed, 0);

    this.timerId = setTimeout(() => this.evaluateTimeout(), remaining);
  }

  private evaluateTimeout(): void {
    const idleDuration = Date.now() - this.lastActivityTimestamp;

    if (idleDuration >= this.inactivityLimit) {
      this.triggerLogout('timeout');
      return;
    }

    this.scheduleNextCheck();
  }

  private onStorageEvent(event: StorageEvent): void {
    if (!event.key) {
      return;
    }

    if (event.key === this.logoutStorageKey) {
      this.triggerLogout('storage');
      return;
    }

    if (event.key === this.activityStorageKey && event.newValue) {
      const timestamp = Number(event.newValue);

      if (Number.isFinite(timestamp)) {
        this.handleRemoteActivity(timestamp);
      }
    }
  }

  private triggerLogout(reason: InactivityLogoutReason): void {
    this.stopWatching();

    this.ngZone.run(() => {
      this.logoutCallback?.(reason);
    });
  }

  private persistActivity(timestamp: number): void {
    localStorage.setItem(this.activityStorageKey, timestamp.toString());
  }

  ngOnDestroy(): void {
    this.stopWatching();
  }
}
